/* eslint-disable max-lines-per-function */
import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  setError,
  setLoaded,
  setLoading,
  withCallState,
  withDevtools,
} from '@angular-architects/ngrx-toolkit';

import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import {
  addEntities,
  addEntity,
  removeAllEntities,
  removeEntity,
  setEntities,
  setEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  distinctUntilChanged,
  exhaustMap,
  forkJoin,
  map,
  of,
  pipe,
  switchMap,
  tap,
} from 'rxjs';

import { Classificator } from '@modules/rtudata/rtudata.service';

import {
  Pallet,
  PalletRequest,
  Seeding,
  SeedingParams,
  SeedingRequest,
  SeedingService,
} from './seeding.service';

export interface SeedingSlot {
  id: string;
  isEmpty: boolean;
  palletId: number;
  seedingId: number;
}

const initialState: {
  selectedSeedingSlots: SeedingSlot[];
  slotSelectOption: 'full' | 'empty' | 'none';
  changesCounter: number; // tracking successful changes after http requests for effect in map component
  pagination: {
    seedingCount: number;
    offset: number;
    limit: number;
  };
  activeTab: 'active' | 'archived';
  speciesFilter: Classificator | null;
} = {
  activeTab: 'active',
  changesCounter: 0,
  pagination: {
    limit: 100,
    offset: 0,
    seedingCount: 0,
  },
  selectedSeedingSlots: [],
  slotSelectOption: 'none',
  speciesFilter: null,
};

// eslint-disable-next-line @typescript-eslint/naming-convention
export const SeedingStore = signalStore(
  { providedIn: 'root' },
  withDevtools('seedingStore'),
  withState(initialState),
  withEntities<Seeding>(),
  withCallState(),
  withProps(() => ({
    router: inject(Router),
    seedingService: inject(SeedingService),
  })),
  withComputed(({ selectedSeedingSlots }) => ({
    selectedSeedingSlotsIds: computed(
      () => new Set(selectedSeedingSlots().map((slot) => slot.id)),
    ),
  })),
  withComputed(({ entities, pagination }) => ({
    hasMore: computed(() => entities().length < pagination().seedingCount),

    sortedEntities: computed(() => entities().sort((a, b) => a.id - b.id)),
  })),
  //API CALL METHODS
  withMethods(({ seedingService, router, ...store }) => ({
    addPallets: rxMethod<PalletRequest[]>(
      pipe(
        distinctUntilChanged(),
        tap(() => {
          patchState(store, setLoading());
        }),
        switchMap((selectedPallets) =>
          seedingService.createPallets(selectedPallets).pipe(
            switchMap(
              () => seedingService.getSeedingById(selectedPallets[0].seeding), //get first seeding id since all are the same
            ),
            tapResponse({
              complete: () => {
                patchState(store, setLoaded());
                void router.navigate(['/', 'seeding']);
              },
              error: (error) => {
                patchState(store, setError(error));
              },
              next: (updatedSeeding) => {
                patchState(store, setEntity(updatedSeeding), {
                  changesCounter: store.changesCounter() + 1,
                  selectedSeedingSlots: [],
                });
              },
            }),
          ),
        ),
      ),
    ),

    addSeeding: rxMethod<SeedingRequest>(
      pipe(
        distinctUntilChanged(),
        tap(() => {
          patchState(store, setLoading());
        }),
        switchMap((param) =>
          seedingService.createSeeding(param).pipe(
            switchMap((seeding) => {
              if (store.selectedSeedingSlots().length === 0) {
                return of({ pallets: null, seeding });
              }

              const palletRequests: PalletRequest[] = store
                .selectedSeedingSlots()
                .map((slot) => ({
                  seeding: seeding.id,
                  territory: slot.id,
                }));

              return seedingService.createPallets(palletRequests).pipe(
                map((createdPallets) => ({
                  pallets: createdPallets,
                  seeding,
                })),
              );
            }),
            tapResponse({
              complete: () => {
                patchState(store, setLoaded());
                void router.navigate(['/', 'seeding']);
              },
              error: (error) => {
                patchState(store, setError(error));
              },
              next: (response) => {
                patchState(store, addEntity(response.seeding), {
                  changesCounter: store.changesCounter() + 1,
                  selectedSeedingSlots: [],
                });
              },
            }),
          ),
        ),
      ),
    ),

    deletePallets: rxMethod<SeedingSlot[]>(
      pipe(
        distinctUntilChanged(),
        tap(() => {
          patchState(store, setLoading());
        }),
        switchMap((selectedSlots) => {
          const param = selectedSlots.map((slot) => ({
            id: slot.palletId,
          }));
          return seedingService.deletePallets(param).pipe(
            switchMap(() =>
              //wait for all the seedings
              forkJoin(
                [...new Set(selectedSlots.map((slot) => slot.seedingId))].map(
                  (seedingId) => seedingService.getSeedingById(seedingId),
                ),
              ),
            ),
            tapResponse({
              complete: () => {
                patchState(store, setLoaded());
                void router.navigate(['/', 'seeding']);
              },
              error: (error) => {
                patchState(store, setError(error));
              },
              next: (updatedSeedings) => {
                patchState(store, setEntities(updatedSeedings), {
                  changesCounter: store.changesCounter() + 1,
                  selectedSeedingSlots: [],
                });
              },
            }),
          );
        }),
      ),
    ),

    deleteSeeding: rxMethod<number>(
      pipe(
        distinctUntilChanged(),
        tap(() => {
          patchState(store, setLoading());
        }),
        switchMap((param) =>
          seedingService.deleteSeeding(param).pipe(
            tapResponse({
              complete: () => {
                patchState(store, setLoaded());
                void router.navigate(['/', 'seeding']);
              },
              error: (error) => {
                patchState(store, setError(error));
              },
              next: () => {
                patchState(store, removeEntity(param), {
                  changesCounter: store.changesCounter() + 1,
                });
              },
            }),
          ),
        ),
      ),
    ),

    loadSeedings: rxMethod<SeedingParams>(
      pipe(
        distinctUntilChanged(),
        tap(() => {
          patchState(store, setLoading());
        }),
        exhaustMap((params) =>
          seedingService.loadSeedings(params).pipe(
            tapResponse({
              complete: () => {
                patchState(store, setLoaded());
              },
              error: (error) => {
                patchState(store, setError(error));
              },
              next: (response) => {
                patchState(store, addEntities(response.results), {
                  pagination: {
                    ...store.pagination(),
                    offset: params.offset + response.results.length,
                    seedingCount: response.count,
                  },
                });
              },
            }),
          ),
        ),
      ),
    ),
    movePallets: rxMethod<{
      selectedPallets: SeedingSlot[];
      destination: SeedingSlot[] | string;
    }>(
      pipe(
        distinctUntilChanged(),
        tap(() => {
          patchState(store, setLoading());
        }),
        switchMap(({ selectedPallets, destination }) => {
          const param: Pallet[] = selectedPallets.map((palletSlot, index) => ({
            id: palletSlot.palletId,
            seeding: palletSlot.seedingId,
            territory:
              typeof destination === 'string'
                ? destination
                : destination[index].id,
          }));

          return seedingService.updatePallets(param).pipe(
            //get updated seeding data
            switchMap(() =>
              forkJoin(
                [...new Set(selectedPallets.map((slot) => slot.seedingId))].map(
                  (seedingId) => seedingService.getSeedingById(seedingId),
                ),
              ),
            ),
            tapResponse({
              complete: () => {
                patchState(store, setLoaded());
                void router.navigate(['/', 'seeding']);
              },
              error: (error) => {
                patchState(store, setError(error));
              },
              next: (movedSeedings) => {
                patchState(store, setEntities(movedSeedings), {
                  changesCounter: store.changesCounter() + 1,
                  selectedSeedingSlots: [],
                });
              },
            }),
          );
        }),
      ),
    ),

    updateSeeding: rxMethod<{ seeding: Seeding }>(
      pipe(
        distinctUntilChanged(),
        tap(() => {
          patchState(store, setLoading());
        }),
        switchMap(({ seeding }) =>
          seedingService.updateSeeding(seeding).pipe(
            tapResponse({
              complete: () => {
                patchState(store, setLoaded());
                void router.navigate(['/', 'seeding']);
              },
              error: (error) => {
                patchState(store, setError(error));
              },
              next: (updatedSeeding) => {
                patchState(store, setEntity(updatedSeeding), {
                  changesCounter: store.changesCounter() + 1,
                });
              },
            }),
          ),
        ),
      ),
    ),
  })),
  //OTHER METHODS TO INTERACT WITH STORE( CAN CLALL API METHODS THAT ARE ABOVE FROM HERE ALSO )
  withMethods(({ ...store }) => ({
    changeSlotSelectOption: (option: 'full' | 'empty' | 'none') => {
      patchState(store, {
        selectedSeedingSlots: [],
        slotSelectOption: option,
      });
    },
    reloadSeedings: () => {
      patchState(store, removeAllEntities(), {
        pagination: {
          ...store.pagination(),
          offset: 0,
        },
      });

      store.loadSeedings({
        is_archived: store.activeTab() === 'archived',
        limit: store.pagination.limit(),
        offset: 0,
        species: store.speciesFilter()?.id,
      });
    },
    setActiveTab: (tab: 'active' | 'archived') => {
      patchState(store, removeAllEntities(), {
        activeTab: tab,
        pagination: {
          ...store.pagination(),
          offset: 0,
        },
      });

      store.loadSeedings({
        is_archived: tab === 'archived',
        limit: store.pagination.limit(),
        offset: 0,
        species: store.speciesFilter()?.id,
      });
    },
    setSeeding: (seeding: Seeding): void => {
      patchState(store, setEntity(seeding));
    },

    setSpeciesFilter: (species: Classificator | null) => {
      patchState(store, removeAllEntities(), {
        pagination: {
          ...store.pagination(),
          offset: 0,
        },
        speciesFilter: species,
      });

      store.loadSeedings({
        is_archived: store.activeTab() === 'archived',
        limit: store.pagination.limit(),
        offset: 0,
        species: species?.id,
      });
    },

    updateSelectedSlots: (slots: SeedingSlot[]) => {
      patchState(store, { selectedSeedingSlots: slots });
    },
  })),

  withHooks({
    onInit: (store) => {
      store.loadSeedings({
        is_archived: false,
        limit: store.pagination.limit(),
        offset: 0,
        species: null,
      });
    },
  }),
);
